/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, size as floatingSize, useFloating } from '@floating-ui/react';

import type { SelectMultipleProps, SelectOptionData, SelectOptionProps, SelectProps } from '../../types';
import { Icon } from '../Icon';
import './Select.less';

// ---------------------------------------------------------------------------
// Select.Option — marker component; Select reads its props via React.Children.
// ---------------------------------------------------------------------------
const Option: React.FC<SelectOptionProps> = () => null;
Option.displayName = 'Select.Option';

interface InternalOption extends SelectOptionData {
  /** Rendered display node (children for the children API, label for options API). */
  display: React.ReactNode;
}

const ChevronIcon = () => (
  <svg className="select__arrow" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path
      d="M3 4.5 6 7.5 9 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const defaultFilter = (input: string, option: SelectOptionData): boolean => {
  const needle = input.toLowerCase();
  if (typeof option.label === 'string' && option.label.toLowerCase().includes(needle)) return true;
  return option.value.toLowerCase().includes(needle);
};

const SelectImpl: React.FC<SelectProps> = ({
  value,
  defaultValue,
  onChange,
  placeholder,
  disabled = false,
  invalid = false,
  mode = 'single',
  filterable = false,
  filterOption,
  options,
  children,
  size = 'medium',
  name,
  id,
  className,
  'aria-label': ariaLabel,
}) => {
  const isMultiple = mode === 'multiple';
  const isCombobox = mode === 'combobox';

  // ---- Resolve options from children or options prop -----------------------
  const resolvedOptions = React.useMemo<InternalOption[]>(() => {
    if (children !== undefined) {
      const out: InternalOption[] = [];
      React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        const props = child.props as Partial<SelectOptionProps>;
        if (props.value === undefined) return;
        out.push({
          value: props.value,
          label: undefined,
          disabled: !!props.disabled,
          display: props.children,
        });
      });
      return out;
    }
    return (options ?? []).map((o) => ({ ...o, display: o.label ?? o.value }));
  }, [children, options]);

  // ---- Single-mode controlled vs uncontrolled value -----------------------
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = React.useState<string>(
    !isMultiple ? (defaultValue as string | undefined) ?? '' : ''
  );
  const currentValue = isControlled ? (value as string) : innerValue;
  const selectedOption = resolvedOptions.find((o) => o.value === currentValue);

  // ---- Multiple-mode controlled vs uncontrolled values --------------------
  const isMultiControlled = isMultiple && value !== undefined;
  const [innerMultiValues, setInnerMultiValues] = React.useState<string[]>(
    isMultiple ? (defaultValue as string[] | undefined) ?? [] : []
  );
  const currentMultiValues: string[] = isMultiple ? (isMultiControlled ? (value as string[]) : innerMultiValues) : [];

  // ---- Open / highlight / search state ------------------------------------
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [searchText, setSearchText] = React.useState('');

  const filterPredicate = filterOption ?? defaultFilter;

  const visibleOptions = React.useMemo(() => {
    if (isCombobox) {
      return searchText ? resolvedOptions.filter((o) => filterPredicate(searchText, o)) : resolvedOptions;
    }
    if ((filterable || isMultiple) && searchText) {
      return resolvedOptions.filter((o) => filterPredicate(searchText, o));
    }
    return resolvedOptions;
  }, [resolvedOptions, isCombobox, isMultiple, filterable, searchText, filterPredicate]);

  // ---- Refs --------------------------------------------------------------
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // ---- Floating-UI: fixed positioning that escapes overflow ancestors -----
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip(),
      floatingSize({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, { width: `${rects.reference.width}px` });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Expose a triggerRef alias so the rest of the component stays unchanged.
  const triggerRef = refs.reference as React.MutableRefObject<HTMLElement | null>;

  // ---- Open / close ------------------------------------------------------
  const open = React.useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(-1);
  }, [disabled]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (!isCombobox) setSearchText('');
  }, [isCombobox]);

  // ---- Outside click closes the popover -----------------------------------
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check both the trigger wrapper and the floating popover (which is no
      // longer a DOM child of wrapperRef when rendered via fixed positioning).
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        refs.floating.current &&
        !refs.floating.current.contains(target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, close, refs.floating]);

  // ---- Focus the search input when filterable popover opens --------------
  React.useEffect(() => {
    if (isOpen && (filterable || isMultiple) && !isCombobox && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, filterable, isMultiple, isCombobox]);

  // ---- Single-mode select handler ----------------------------------------
  const selectValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setInnerValue(next);
      (onChange as ((v: string) => void) | undefined)?.(next);
      setSearchText('');
      close();
    },
    [isControlled, onChange, close]
  );

  // ---- Multiple-mode toggle handler --------------------------------------
  const toggleMultiValue = React.useCallback(
    (val: string) => {
      const next = currentMultiValues.includes(val)
        ? currentMultiValues.filter((v) => v !== val)
        : [...currentMultiValues, val];
      if (!isMultiControlled) setInnerMultiValues(next);
      (onChange as SelectMultipleProps['onChange'])?.(next);
      // intentionally do NOT close the popover
    },
    [currentMultiValues, isMultiControlled, onChange]
  );

  const handleOptionClick = (opt: InternalOption) => {
    if (opt.disabled) return;
    if (isMultiple) {
      toggleMultiValue(opt.value);
    } else {
      selectValue(opt.value);
    }
  };

  // ---- Keyboard handler --------------------------------------------------
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (e.key === 'Backspace' && isMultiple && !searchText) {
      if (currentMultiValues.length > 0) {
        const next = currentMultiValues.slice(0, -1);
        if (!isMultiControlled) setInnerMultiValues(next);
        (onChange as SelectMultipleProps['onChange'])?.(next);
      }
      return;
    }

    if (e.key === 'Escape') {
      if (isOpen) {
        e.preventDefault();
        close();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        open();
        setHighlightedIndex(0);
        return;
      }
      setHighlightedIndex((prev) => {
        for (let offset = 1; offset <= visibleOptions.length; offset++) {
          const next = (prev + offset) % visibleOptions.length;
          if (!visibleOptions[next].disabled) return next;
        }
        return prev;
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        open();
        setHighlightedIndex(Math.max(0, visibleOptions.length - 1));
        return;
      }
      setHighlightedIndex((prev) => {
        const start = prev < 0 ? visibleOptions.length : prev;
        for (let offset = 1; offset <= visibleOptions.length; offset++) {
          const next = (start - offset + visibleOptions.length) % visibleOptions.length;
          if (!visibleOptions[next].disabled) return next;
        }
        return prev;
      });
      return;
    }
    if (e.key === 'Enter') {
      if (isOpen && highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
        e.preventDefault();
        const opt = visibleOptions[highlightedIndex];
        if (!opt.disabled) handleOptionClick(opt);
      }
      return;
    }
  };

  // ---- Combobox input change ---------------------------------------------
  const handleComboboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (!isControlled) setInnerValue(next);
    setSearchText(next);
    (onChange as ((v: string) => void) | undefined)?.(next);
    if (!isOpen) setIsOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setHighlightedIndex(-1);
  };

  // ---- Trigger content (single mode) -------------------------------------
  const triggerContent = (() => {
    if (selectedOption) {
      return <span className="select__value">{selectedOption.display}</span>;
    }
    if (placeholder) {
      return <span className="select__placeholder">{placeholder}</span>;
    }
    return null;
  })();

  return (
    <div
      ref={wrapperRef}
      className={cx(
        'select',
        `select--${size}`,
        {
          'select--invalid': invalid,
          'select--disabled': disabled,
          'select--open': isOpen,
          'select--combobox': isCombobox,
          'select--multiple': isMultiple,
        },
        className
      )}>
      {isCombobox ? (
        <input
          ref={(node) => {
            refs.setReference(node);
          }}
          id={id}
          name={name}
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          className="select__trigger select__trigger--combobox"
          value={currentValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleComboboxChange}
          onFocus={open}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <div
          ref={(node) => {
            refs.setReference(node);
          }}
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-multiselectable={isMultiple || undefined}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className="select__trigger"
          onClick={() => (isOpen ? close() : open())}
          onKeyDown={handleKeyDown}>
          {isMultiple ? (
            <div className="select__pills">
              {currentMultiValues.length === 0
                ? placeholder && <span className="select__placeholder">{placeholder}</span>
                : currentMultiValues.map((v) => {
                    const opt = resolvedOptions.find((o) => o.value === v);
                    return (
                      <button
                        key={v}
                        type="button"
                        className="select__pill"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          toggleMultiValue(v);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Remove ${opt?.display ?? v}`}>
                        <span className="select__pill-label">{opt?.display ?? v}</span>
                        <Icon name="actionable/close" size="small" />
                      </button>
                    );
                  })}
            </div>
          ) : (
            triggerContent
          )}
          <ChevronIcon />
        </div>
      )}

      {/* Hidden value carrier for form serialization in non-combobox mode */}
      {!isCombobox && !isMultiple && name && <input type="hidden" name={name} value={currentValue} />}

      {isOpen && (
        <FloatingPortal>
          <div ref={refs.setFloating} className="select__popover" style={floatingStyles}>
            {(filterable || isMultiple) && !isCombobox && (
              <div className="select__search">
                <input
                  ref={searchInputRef}
                  role="searchbox"
                  aria-label="Filter options"
                  className="select__search-input"
                  value={searchText}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
            <ul role="listbox" className="select__listbox">
              {visibleOptions.length === 0 ? (
                <li className="select__empty">No matches</li>
              ) : (
                visibleOptions.map((opt, i) => {
                  const selected = isMultiple ? currentMultiValues.includes(opt.value) : opt.value === currentValue;
                  const highlighted = i === highlightedIndex;
                  return (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={opt.disabled}
                      className={cx('select__option', {
                        'select__option--selected': selected,
                        'select__option--highlighted': highlighted,
                        'select__option--disabled': opt.disabled,
                      })}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleOptionClick(opt)}>
                      {isMultiple && (
                        <span className="select__option-check">
                          {selected && <Icon name="actionable/tick" size="small" />}
                        </span>
                      )}
                      {opt.display}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

SelectImpl.displayName = 'Select';

type SelectComponent = React.FC<SelectProps> & { Option: React.FC<SelectOptionProps> };

const Select = SelectImpl as SelectComponent;
Select.Option = Option;

export default Select;
